import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase, initializeSupabase } from '../config/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';

const AuthContext = createContext({});

// ID unique pour la notification de connexion
const LOGIN_NOTIFICATION_ID = 'login-notification';

// Fonction utilitaire pour gérer les cookies
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supabaseInstance, setSupabaseInstance] = useState(null);
  const notificationShown = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const supabaseClient = await initializeSupabase();
        setSupabaseInstance(supabaseClient);

        // Vérifier la session au chargement
        const { data: { session } } = await supabaseClient.auth.getSession();
        setUser(session?.user ?? null);
        setLoading(false);

        // Ne rediriger que si l'utilisateur n'est pas déjà sur une page protégée
        if (session?.user && location.pathname === '/auth') {
          navigate('/dashboard');
        }

        // Écouter les changements d'authentification
        const { data: { subscription } } = supabaseClient.auth.onAuthStateChange((_event, session) => {
          setUser(session?.user ?? null);
          setLoading(false);

          if (_event === 'SIGNED_IN') {
            if (!notificationShown.current) {
              toast.success('Connexion réussie !');
              notificationShown.current = true;
            }

            // Ne rediriger que si on est sur la page d'auth
            if (location.pathname === '/auth') {
              navigate('/dashboard');
            }
          } else if (_event === 'SIGNED_OUT') {
            toast.success('Déconnexion réussie !');
            notificationShown.current = false;
            navigate('/auth');
          }
        });

        return () => subscription.unsubscribe();
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de l\'authentification:', error);
        setLoading(false);
      }
    };

    initAuth();
  }, [navigate, location.pathname]);

  const signIn = async (email, password) => {
    if (!supabaseInstance) throw new Error('Supabase non initialisé');
    const { error } = await supabaseInstance.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email, password) => {
    if (!supabaseInstance) throw new Error('Supabase non initialisé');
    const { error } = await supabaseInstance.auth.signUp({ 
      email, 
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
  };

  const signOut = async () => {
    if (!supabaseInstance) throw new Error('Supabase non initialisé');
    const { error } = await supabaseInstance.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    supabase: supabaseInstance
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
}; 